import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { Fragment, ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { CompanySystemSettings } from "./companySettingsMobileView/companySystemSettings/companySystemSettings.component";
import { CompanySubscription } from "./companySubscription/companySubscription.component";
import { CompanyUsers } from "./companyUsers/companyUsers.component";
import { CustomerSettings } from "./customerSettings/customerSettings.component";
import { DetailsSettings } from "./detailsSettings/detailsSettings.component";
import { ObSettings } from "./ObSettings/obSettings.component";
import { OrganizationSettings } from "./organizationSettings/organizationSettings.component";
import { ProfileSettings } from "./profileSettings/profileSettings.component";
import {
  CompanySettingsV2PageKey,
  getCompanySettingsV2MenuItems,
  parseCompanySettingsV2Page,
} from "./companySettingsV2.menu.logic";
import {
  SettingsV2Content,
  SettingsV2Layout,
  SettingsV2Menu,
  SettingsV2MenuButton,
  SettingsV2MenuLabel,
  SettingsV2Shell,
  SettingsV2Sidebar,
  SettingsV2Support,
  SettingsV2SupportButton,
  SettingsV2SupportButtonIcon,
  SettingsV2SupportText,
  SettingsV2SupportTitle,
} from "./companySettingsV2.styled";
import {
  isFreeSubscription,
  isSubscriptionLoadedForCompany,
} from "web-apps/company/utils/subscription.logic";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { StatusBanner } from "components/designV2/statusBanner/statusBanner";

const DEFAULT_PAGE: CompanySettingsV2PageKey = "profile";

const renderSettingsContent = (
  page: CompanySettingsV2PageKey,
): ReactElement => {
  switch (page) {
    case "profile":
      return <ProfileSettings />;
    case "details":
      return <DetailsSettings view="desktop" />;
    case "users":
      return <CompanyUsers view="desktop" />;
    case "organization":
      return <OrganizationSettings />;
    case "Customers":
      return <CustomerSettings />;
    case "Ob":
      return <ObSettings />;
    case "Subscription":
      return <CompanySubscription />;
    case "System":
      return <CompanySystemSettings />;
  }
};

export const CompanySettingsV2 = (): ReactElement | null => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();
  const [companyState] = useCompanyStore();
  const isFree = isFreeSubscription(companyState.subscription.info);
  const isSubscriptionRestricted =
    companyState.company?.id != null &&
    (!isSubscriptionLoadedForCompany({
      companyId: companyState.company.id,
      isLoading: companyState.subscription.isLoading,
      subscription: companyState.subscription.info,
    }) ||
      isFree);
  const requestedPage = new URLSearchParams(location.search).get("page");

  const menuItems = useMemo(() => {
    return getCompanySettingsV2MenuItems(
      t,
      companyState.companySettings,
      isSubscriptionRestricted,
    );
  }, [companyState.companySettings, isSubscriptionRestricted, t]);

  const activePage = useMemo((): CompanySettingsV2PageKey => {
    const params = new URLSearchParams(location.search);
    const requestedPage = parseCompanySettingsV2Page(params.get("page"));
    const firstAvailablePage = menuItems[0]?.page ?? DEFAULT_PAGE;

    if (
      requestedPage &&
      menuItems.some((item) => item.page === requestedPage)
    ) {
      return requestedPage;
    }

    return firstAvailablePage;
  }, [location.search, menuItems]);

  const handleMenuClick = (page: CompanySettingsV2PageKey): void => {
    history.push(`${path}?page=${page}`);
  };

  if (
    (activePage === "Ob" || activePage === "System") &&
    !isSubscriptionLoadedForCompany({
      companyId: companyState.company?.id,
      isLoading: companyState.subscription.isLoading,
      subscription: companyState.subscription.info,
    })
  ) {
    return companyState.subscription.isLoading ||
      companyState.company == null ? null : (
      <StatusBanner
        iconName="v2/alert"
        label={t("Subscription.LoadFailed")}
        textColor={ColorV2.NegativeRed}
        backgroundColor={ColorV2.White}
      />
    );
  }

  if (isFree && requestedPage === "Ob") {
    return <Redirect to="/company/schedule" />;
  }

  return (
    <SettingsV2Shell>
      <SettingsV2Layout>
        <SettingsV2Sidebar>
          <SettingsV2Menu aria-label={t("General.Settings")}>
            {menuItems.map((item) => {
              const isActive = item.page === activePage;
              const isDisabled = item.disabledReason != null;
              const iconColor =
                isActive && !isDisabled ? ColorV2.Blue1 : ColorV2.Blue5;

              const menuButton = (
                <SettingsV2MenuButton
                  type="button"
                  $isActive={isActive}
                  disabled={isDisabled}
                  onClick={() => handleMenuClick(item.page)}
                >
                  <CustomIcon
                    name={item.iconName}
                    size="24px"
                    color={iconColor}
                  />
                  <SettingsV2MenuLabel>{item.label}</SettingsV2MenuLabel>
                </SettingsV2MenuButton>
              );

              return isDisabled ? (
                <TooltipHover
                  key={item.page}
                  text={item.disabledReason}
                  fullWidth
                >
                  {menuButton}
                </TooltipHover>
              ) : (
                <Fragment key={item.page}>{menuButton}</Fragment>
              );
            })}
          </SettingsV2Menu>
          <SettingsV2Support
            type="button"
            onClick={() => {
              window.open("https://www.simflexity.com/sv/kontakt");
            }}
          >
            <SettingsV2SupportTitle>
              {t("CompanySettingsV2.HelpTitle")}
            </SettingsV2SupportTitle>
            <SettingsV2SupportText>
              {t("CompanySettingsV2.HelpText")}
            </SettingsV2SupportText>
            <SettingsV2SupportButton>
              <SettingsV2SupportButtonIcon>
                <CustomIcon
                  name="v2/envelope"
                  size="20px"
                  color={ColorV2.White}
                />
              </SettingsV2SupportButtonIcon>
              {t("CompanySettingsV2.ContactUs")}
            </SettingsV2SupportButton>
          </SettingsV2Support>
        </SettingsV2Sidebar>
        <SettingsV2Content $isFrameless={activePage === "System"}>
          {renderSettingsContent(activePage)}
        </SettingsV2Content>
      </SettingsV2Layout>
    </SettingsV2Shell>
  );
};
